import * as React from 'react';
import { MoreHorizontal } from 'lucide-react';
import { ReactComponent as ArrowIcon } from '@blink/assets/src/icons/arrow.svg';

import { ButtonProps, buttonVariants } from '../Button';

import { cn } from '@/shared/lib/tailwind-merge';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
    <nav
        role='navigation'
        aria-label='pagination'
        className={cn('flex justify-center', className)}
        {...props}
    />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
    ({ className, ...props }, ref) => (
        <ul ref={ref} className={cn('flex flex-row items-center', className)} {...props} />
    ),
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
    ({ className, ...props }, ref) => <li ref={ref} className={cn('', className)} {...props} />,
);
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
    isActive?: boolean;
} & Pick<ButtonProps, 'size'> &
    React.ComponentProps<'a'>;

const PaginationLink = ({
    className,
    isActive,
    size = 'icon',
    ...props
}: PaginationLinkProps & { disabled?: boolean }) => {
    const { disabled, ...restProps } = props;

    return (
        <a
            aria-current={isActive ? 'page' : undefined}
            className={cn(
                buttonVariants({
                    variant: isActive ? 'outline' : 'ghost',
                    size,
                }),
                disabled ? 'pointer-events-none [&_path]:fill-content-stateDisable2' : '',
                'flex !w-[36px] !font-semibold items-center justify-center rounded-[4px] bg-white !text-md transition-colors hover:bg-background-hoverDarkOverlay2 hover:!text-content-primary',
                className,
            )}
            {...restProps}
        />
    );
};
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
    className,
    ...props
}: React.ComponentProps<typeof PaginationLink>) => (
    <PaginationLink
        aria-label='Go to previous page'
        size='default'
        className={cn(
            'flex h-[36px] items-center py-0 justify-center rounded-[4px] disabled:text-content-fourth bg-white text-md font-normal transition-colors hover:bg-background-hoverDarkOverlay2 hover:!text-content-primary gap-2 px-5 w-auto',
            className,
        )}
        {...props}
    >
        <ArrowIcon className='rotate-90' />
    </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => (
    <PaginationLink
        aria-label='Go to next page'
        size='default'
        className={cn(
            'flex h-[36px] items-center py-0 justify-center rounded-[4px] bg-white text-md font-normal transition-colors hover:bg-background-hoverDarkOverlay2 hover:!text-content-primary gap-2 px-5 w-auto',
            className,
        )}
        {...props}
    >
        <ArrowIcon className='-rotate-90' />
    </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
    <span
        aria-hidden
        className={cn('flex h-9 w-9 items-center justify-center', className)}
        {...props}
    >
        <MoreHorizontal className='h-4 w-4' />
        <span className='sr-only'>More pages</span>
    </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
    Pagination,
    PaginationContent,
    PaginationLink,
    PaginationItem,
    PaginationPrevious,
    PaginationNext,
    PaginationEllipsis,
};
